import * as Yup from "yup";

import {
  CREDIT_NOTE,
  CREDIT_NOTE_LOT,
  DEBIT_NOTE,
  DEBIT_NOTE_LOT,
  INVOICE_DOCUMENT,
  NORMAL_PROFORMA,
  PROFORMA_INVOICE_DOCUMENT,
  RETURN_GUIDE_DOCUMENT,
  TRANSPORT_DOCUMENT,
} from "../../helpers/constants";

/**
 * Validation schema of the invoicing form, kept in its own module so it can be
 * exercised directly: importing InvoicingForm pulls helpers/utils ->
 * @react-pdf/renderer, which does not load under jsdom.
 *
 * @param {(key: string, vars?: object) => string} t translation function
 */
export const getInvoicingValidationSchema = (t) =>
  Yup.object({
    tax_exemption_code: Yup.string().when("dont_apply_vat", {
      is: (dont_apply_vat) => {
        return dont_apply_vat;
      },
      then: Yup.string().required(
        `${t("common.please")} ${t("common.select")} ${t("common.taxExemptionCode")}`
      ),
    }),
    reg_type: Yup.string().when("document_type", {
      is: (document_type) =>
        [CREDIT_NOTE, CREDIT_NOTE_LOT, DEBIT_NOTE, DEBIT_NOTE_LOT].includes(document_type?.description),
      then: Yup.string().required(
        `${t("common.please")} ${t("common.select")} ${t("common.CreditNoteReason")}`
      ),
    }),
    date: Yup.date()
      .typeError(
        t("validation.formatGeneral", {
          enterOrSelect: t("common.select"),
          field: t("common.date"),
        })
      )
      .required(`${t("common.please")} ${t("common.select")} ${t("common.date")}`),
    customer: Yup.object()
      .required(`${t("common.please")} ${t("common.select")} ${t("common.customer")}`)
      .nullable(),
    payment_condition: Yup.object()
      .when("document_type", {
        is: (document_type) =>
          [CREDIT_NOTE, DEBIT_NOTE, CREDIT_NOTE_LOT, DEBIT_NOTE_LOT].includes(document_type?.description),
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.paymentConditions")}`)
          .nullable(),
      })
      .nullable(),
    document_type: Yup.object()
      .required(`${t("common.please")} ${t("common.select")} ${t("common.typeOfDocument")}`)
      .nullable(),
    payment_type: Yup.object()
      .when("document_type", {
        is: (document_type) =>
          ![
            TRANSPORT_DOCUMENT,
            RETURN_GUIDE_DOCUMENT,
            PROFORMA_INVOICE_DOCUMENT,
            INVOICE_DOCUMENT,
            NORMAL_PROFORMA,
            DEBIT_NOTE,
            CREDIT_NOTE,
            CREDIT_NOTE_LOT,
            DEBIT_NOTE_LOT,
          ].includes(document_type?.description),
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.paymentMethod")}`)
          .nullable(),
      })
      .nullable(),
    // debit_total: Yup.number().when("document_type", {
    //   is: (document_type) =>
    //     [DEBIT_NOTE,DEBIT_NOTE_LOT].includes(document_type?.description),
    //   then: Yup.number().required(
    //     `${t("common.please")} ${t("common.enter")} ${t(
    //       "common.totalAmount"
    //     )}`
    //   ).min(0, `${t("common.please")} ${t("common.enter")} ${t("common.totalAmount" )}`),
    // }),
    warehouse: Yup.object()
      .when("document_type", {
        is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
        then: Yup.object()
          .required(`${t("common.please")} ${t("common.select")} ${t("common.warehouse")}`)
          .nullable(),
      })
      .nullable(),
    loading_date: Yup.date().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.date()
        .typeError(
          t("validation.formatGeneral", {
            enterOrSelect: t("common.select"),
            field: t("common.date"),
          })
        )
        .required(`${t("common.please")} ${t("common.select")} ${t("common.date")}`),
    }),
    unloading_date: Yup.date().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.date()
        .typeError(
          t("validation.formatGeneral", {
            enterOrSelect: t("common.select"),
            field: t("common.date"),
          })
        )
        .required(`${t("common.please")} ${t("common.select")} ${t("common.date")}`),
    }),
    // car_registration_plate: Yup.string().when("document_type", {
    //   is: (document_type) =>
    //     [TRANSPORT_DOCUMENT].includes(document_type?.description),
    //   then: Yup.string().required(
    //     t("validation.formatGeneral", {
    //       enterOrSelect: t("common.enter"),
    //       field: t("common.vehicleRegistrationPlateNo"),
    //     })
    //   ),
    // }),
    loading_address: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string().required(
        t("validation.formatGeneral", {
          enterOrSelect: t("common.enter"),
          field: t("common.loadingAddress"),
        })
      ),
    }),
    loading_city: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string()
        .required(
          t("validation.formatGeneral", {
            enterOrSelect: t("common.enter"),
            field: t("common.loadingCity"),
          })
        )
        .nullable(),
    }),
    // loading_postcode: Yup.string().when("document_type", {
    //   is: (document_type) =>
    //     [TRANSPORT_DOCUMENT].includes(document_type?.description),
    //   then: Yup.string().required(
    //     t("validation.formatGeneral", {
    //       enterOrSelect: t("common.enter"),
    //       field: t("common.loadingPostcode"),
    //     })
    //   ).nullable(),
    // }),
    loading_postcode: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string()
        .required(
          t("validation.formatGeneral", {
            enterOrSelect: t("common.enter"),
            field: t("common.loadingPostcode"),
          })
        )
        .test("no-spaces", `${t("common.enterNoSpaces")}`, (value) => !/\s/.test(value || "")),
    }),
    loading_country: Yup.object()
      .when("document_type", {
        is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
        then: Yup.object()
          .required(
            t("validation.formatGeneral", {
              enterOrSelect: t("common.select"),
              field: t("common.loadingCountry"),
            })
          )
          .nullable(),
      })
      .nullable(),
    unloading_address: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string().required(
        t("validation.formatGeneral", {
          enterOrSelect: t("common.enter"),
          field: t("common.unLoadingAddress"),
        })
      ),
    }),
    unloading_city: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string().required(
        t("validation.formatGeneral", {
          enterOrSelect: t("common.enter"),
          field: t("common.unLoadingCity"),
        })
      ),
    }),
    // unloading_postcode: Yup.string().when("document_type", {
    //   is: (document_type) =>
    //     [TRANSPORT_DOCUMENT].includes(document_type?.description),
    //   then: Yup.string().required(
    //     t("validation.formatGeneral", {
    //       enterOrSelect: t("common.enter"),
    //       field: t("common.unLoadingPostcode"),
    //     })
    //   ),
    // }),
    unloading_postcode: Yup.string().when("document_type", {
      is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
      then: Yup.string()
        .required(
          t("validation.formatGeneral", {
            enterOrSelect: t("common.enter"),
            field: t("common.unLoadingPostcode"),
          })
        )
        .test("no-spaces", `${t("common.enterNoSpaces")}`, (value) => !/\s/.test(value || "")),
    }),
    unloading_country: Yup.object()
      .when("document_type", {
        is: (document_type) => [TRANSPORT_DOCUMENT].includes(document_type?.description),
        then: Yup.object()
          .required(
            t("validation.formatGeneral", {
              enterOrSelect: t("common.select"),
              field: t("common.unLoadingCountry"),
            })
          )
          .nullable(),
      })
      .nullable(),
  });

export default getInvoicingValidationSchema;
